import { Input } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import { calendarDay } from '@/shared/lib/due-date'

export interface DueDatePickerProps {
  value: string | null
  onChange: (date: string | null) => void
  label?: string
  /** Botones de atajo (hoy / mañana / en una semana / quitar). */
  withShortcuts?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Los atajos usan `calendarDay`, que es la MISMA fuente de "hoy" que el badge
 * de vencimiento y que el recorte del tablero. Antes esto armaba el día con los
 * componentes locales del `Date`: coincidía por casualidad y dejaba de coincidir
 * en cuanto alguien miraba con otra timezone.
 */

export function DueDatePicker({
  value,
  onChange,
  label,
  withShortcuts = false,
  disabled,
  className,
}: DueDatePickerProps) {
  const t = useT()

  // Los atajos son NUESTRA redacción, así que se traducen; "hoy", "mañana" y
  // "en una semana" ya existen en el catálogo de vencimientos y salen de ahí
  // para no decir lo mismo de dos maneras distintas en la misma pantalla.
  const shortcuts: { label: string; value: string | null }[] = [
    { label: t('due.relative.today'), value: calendarDay(0) },
    { label: t('due.relative.tomorrow'), value: calendarDay(1) },
    { label: t('due.relative.inWeeks', { count: 1 }), value: calendarDay(7) },
    { label: t('common.action.remove'), value: null },
  ]

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <Input
        type="date"
        aria-label={label ?? t('tasks.field.dueDate')}
        disabled={disabled}
        className="num w-auto min-w-[130px]"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      />
      {withShortcuts
        ? shortcuts.map((shortcut) => (
            <button
              key={shortcut.label}
              type="button"
              disabled={disabled}
              onClick={() => onChange(shortcut.value)}
              className={cn(
                'hairline h-6 rounded-[var(--radius-control)] px-1.5 text-2xs text-text-2',
                'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2 hover:text-text-1',
              )}
            >
              {shortcut.label}
            </button>
          ))
        : null}
    </div>
  )
}
